import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X } from "lucide-react";

import { FETCHING_EDIT_DOWNLOAD, FETCHING_REMOVE_DOWNLOAD } from "@/redux/types/main.types";
import { useDownloads } from "@/hooks/general/useDownloads";
import CustomFetching from "@/components/desktop/fetching/CustomFetching";

import "./Document.scss";

const Document = () => {
	const dispatch = useDispatch();
	const { downloads } = useSelector((state) => state.main);

	const { localDownloads, downloadFile, executeExport, checkExportStatus, updateDownloadState, removeDownload } = useDownloads(downloads);

	useEffect(() => {
		const processDownloads = async () => {
			for (const download of localDownloads) {
				if (!download.inExecution && ["excel", "pdf"].includes(download.type)) {
					await handleDownload(download);
				}
				if (!download.inExecution && !download.inProgress && download.type === "key") {
					await handleExecuteExcel(download);
				}
			}
		};

		processDownloads();

		const handleBeforeUnload = () => {
			if (localDownloads.filter((ld) => ld.type !== "excel").length === 0) {
				return;
			}

			const newDownloads = localDownloads.map((doc) => ({
				...doc,
				inChecked: false,
			}));

			localStorage.setItem("downloads", JSON.stringify(newDownloads));
		};

		window.addEventListener("beforeunload", handleBeforeUnload);

		const savedDownloads = localStorage.getItem("downloads");
		if (savedDownloads) {
			const parsedDownloads = JSON.parse(savedDownloads);
			dispatch({
				type: FETCHING_EDIT_DOWNLOAD,
				payload: {
					downloads: parsedDownloads,
				},
			});
			localStorage.removeItem("downloads");
		}

		const checkDownloadsStatus = async () => {
			if (localDownloads && localDownloads.length !== 0) {
				for (const download of localDownloads) {
					if (download.inProgress && download.type === "key" && !download.inChecked) {
						await handleCheckExcel(download.key, download.api || "lumen");
					}
				}
			}
		};

		const intervalId = setInterval(checkDownloadsStatus, 3000);

		return () => {
			clearInterval(intervalId);
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
		// eslint-disable-next-line
	}, [localDownloads]);

	const handleDownload = async (download) => {
		updateDownloadState(download.key, { inExecution: true });

		const result = await downloadFile(download);

		if (result.success) {
			handleDelete(download.key);
		} else if (result.shouldRetry) {
			updateDownloadState(download.key, { inExecution: false, retries: result.retries });
		} else {
			handleDelete(download.key);
		}
	};

	const handleExecuteExcel = async (download) => {
		updateDownloadState(download.key, { inExecution: true });

		const result = await executeExport(download);

		if (result.success) {
			updateDownloadState(download.key, { inProgress: true });
		} else if (result.shouldRetry) {
			updateDownloadState(download.key, { inExecution: false, retries: result.retries });
		} else {
			handleDelete(download.key);
		}
	};

	const handleCheckExcel = async (uuid, api = "lumen") => {
		updateDownloadState(uuid, { inChecked: true });

		const result = await checkExportStatus(uuid, api);

		if (result.status === "error") {
			handleDelete(uuid);
			return;
		}

		if (result.status === "completed" && result.url) {
			handleDelete(uuid);
			const link = document.createElement("a");
			link.href = result.url;
			link.click();
			return;
		}

		updateDownloadState(uuid, { inChecked: false });
	};

	const handleDelete = (key) => {
		removeDownload(key);

		dispatch({
			type: FETCHING_REMOVE_DOWNLOAD,
			payload: {
				uuid: key,
			},
		});
	};

	const visibleDownloads = localDownloads?.filter((d) => d.show) || [];
	const hasVisibleDownloads = visibleDownloads.length > 0;

	if (!hasVisibleDownloads) {
		return null;
	}

	return (
		<div className="document-notification">
			{visibleDownloads.map((download) => (
				<div key={download.key} className="document-card">
					<CustomFetching size="md" />
					<div className="document-content">
						<span className="document-title">
							Generando documento <i>{download.name}</i>
						</span>
						<small className="document-subtitle">
							{download.retries > 0 && `Reintento ${download.retries}/3 - `}
							esto puede tardar unos minutos, el documento se descargará automáticamente.
						</small>
					</div>
					<button onClick={() => handleDelete(download.key)} className="document-close" aria-label="Cancelar descarga">
						<X size={18} />
					</button>
				</div>
			))}
		</div>
	);
};

export default Document;
